// src/pages/RoomTypesPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

import { Button, Input, Modal, Table } from "antd";
import {
  ACTION_ICONS,
  CardTitle,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconInfo, IconPlus } from "../components/ui/icons";
import notify from "../utils/notify";

const { confirm } = Modal;

const RoomTypesPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const navigate = useNavigate();
  const hotelId = authData?.hotelId;

  const [roomTypes, setRoomTypes] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [showGuide, setShowGuide] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [currentRoomType, setCurrentRoomType] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (hotelId) return;
    navigate("/login");
  }, [hotelId, navigate]);

  const fetchRoomTypes = useCallback(async () => {
    if (!hotelId) return;

    setPageLoading(true);
    try {
      const res = await axiosInstance.get(`/RoomTypes/GetRoomTypes/${hotelId}`);
      setRoomTypes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      notify.error({
        title: "Error",
        description: "Failed to fetch room types.",
        placement: "topRight",
      });
      setRoomTypes([]);
    } finally {
      setPageLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    if (hotelId) fetchRoomTypes();
  }, [hotelId, fetchRoomTypes]);

  const openAdd = useCallback(() => {
    setCurrentRoomType(null);
    setTypeName("");
    setDescription("");
    setEditModalOpen(true);
  }, []);

  const openEdit = useCallback((record) => {
    setCurrentRoomType(record);
    setTypeName(record?.typeName ?? "");
    setDescription(record?.description ?? "");
    setEditModalOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditModalOpen(false);
    setCurrentRoomType(null);
    setTypeName("");
    setDescription("");
  }, []);

  const handleDelete = useCallback(
    async (roomTypeId) => {
      try {
        await axiosInstance.delete(`/RoomTypes/DeleteRoomType/${roomTypeId}`);
        notify.success({
          title: "Success",
          description: "Room type deleted successfully.",
          placement: "topRight",
        });
        fetchRoomTypes();
      } catch {
        notify.error({
          title: "Error",
          description: "Failed to delete room type.",
          placement: "topRight",
        });
      }
    },
    [fetchRoomTypes]
  );

  const handleBatchDelete = useCallback(() => {
    confirm({
      title: `Delete ${selectedRowKeys.length} room type${selectedRowKeys.length === 1 ? "" : "s"}?`,
      content: "This cannot be undone. Rooms already using these types may be affected.",
      centered: true,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map((id) =>
              axiosInstance.delete(`/RoomTypes/DeleteRoomType/${id}`)
            )
          );
          notify.success({
            title: "Success",
            description: `${selectedRowKeys.length} room type(s) deleted successfully.`,
            placement: "topRight",
          });
          setSelectedRowKeys([]);
          fetchRoomTypes();
        } catch {
          notify.error({
            title: "Error",
            description: "Failed to delete room types.",
            placement: "topRight",
          });
        }
      },
    });
  }, [selectedRowKeys, fetchRoomTypes]);

  const handleSave = useCallback(async () => {
    if (!hotelId) return;

    const name = String(typeName || "").trim();
    if (!name) {
      notify.warning({
        title: "Validation",
        description: "Room type name is required.",
        placement: "topRight",
      });
      return;
    }

    setSaving(true);
    try {
      if (currentRoomType?.roomTypeId) {
        await axiosInstance.put(
          `/RoomTypes/UpdateRoomType/${currentRoomType.roomTypeId}`,
          {
            hotelId,
            typeName: name,
            description: String(description || "").trim(),
          }
        );
        notify.success({
          title: "Success",
          description: "Room type updated successfully.",
          placement: "topRight",
        });
      } else {
        await axiosInstance.post(`/RoomTypes/AddRoomType`, {
          hotelId,
          typeName: name,
          description: String(description || "").trim(),
        });
        notify.success({
          title: "Success",
          description: "Room type added successfully.",
          placement: "topRight",
        });
      }

      closeEdit();
      fetchRoomTypes();
    } catch (error) {
      notify.error({
        title: "Error",
        description: error?.response?.data?.message || "Failed to save room type.",
        placement: "topRight",
      });
    } finally {
      setSaving(false);
    }
  }, [hotelId, typeName, description, currentRoomType, closeEdit, fetchRoomTypes]);

  const filteredRoomTypes = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return roomTypes;

    return roomTypes.filter((rt) => {
      const n = String(rt?.typeName ?? "").toLowerCase();
      const d = String(rt?.description ?? "").toLowerCase();
      return n.includes(q) || d.includes(q);
    });
  }, [roomTypes, searchText]);

  const hasSelected = selectedRowKeys.length > 0;

  const columns = useMemo(
    () => [
      {
        title: "Room type",
        dataIndex: "typeName",
        key: "typeName",
        width: 280,
        render: (v, r) => (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{v || "—"}</span>
            <span className="hms-cell-sub">#{r?.roomTypeId}</span>
          </div>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (v) =>
          v ? (
            <span className="hms-cell-ellipsis" title={v}>
              {v}
            </span>
          ) : (
            <span className="hms-muted">—</span>
          ),
      },
      {
        title: "",
        key: "action",
        fixed: "right",
        align: "right",
        width: 110,
        render: (_, record) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RowActions
              actions={[
                { key: "edit", label: "Edit room type", icon: "edit", onClick: () => openEdit(record) },
                {
                  key: "delete",
                  label: "Delete room type",
                  icon: "delete",
                  danger: true,
                  confirm: "Delete this room type?",
                  okText: "Delete",
                  onClick: () => handleDelete(record?.roomTypeId),
                },
              ]}
            />
          </span>
        ),
      },
    ],
    [openEdit, handleDelete]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
    }),
    [selectedRowKeys]
  );

  return (
    <div className="hms-page rooms-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowGuide(true)}>
              Guide
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={openAdd}>
              Add room type
            </Button>
          </>
        }
      />

      <SectionCard
        flush
        title={<CardTitle count={roomTypes.length}>Room types</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchText, onChange: setSearchText, placeholder: "Search room types" }}
            onRefresh={fetchRoomTypes}
            loading={pageLoading}
          >
            {hasSelected && (
              <Button danger icon={ACTION_ICONS.delete} onClick={handleBatchDelete}>
                Delete selected ({selectedRowKeys.length})
              </Button>
            )}
          </ListToolbar>
        }
      >
        <Table
          className="hms-table"
          rowKey="roomTypeId"
          rowSelection={{ type: "checkbox", ...rowSelection }}
          columns={columns}
          dataSource={filteredRoomTypes}
          loading={pageLoading}
          scroll={{ x: 700 }}
          pagination={tablePagination("room type")}
          onRow={(record) => ({ onClick: () => openEdit(record), style: { cursor: "pointer" } })}
          locale={{
            emptyText: (
              <div className="hms-empty">
                No room types yet.{" "}
                <Button type="link" onClick={openAdd}>
                  Add the first one
                </Button>
              </div>
            ),
          }}
        />
      </SectionCard>

      <Modal
        title="Room types guide"
        open={showGuide}
        onCancel={() => setShowGuide(false)}
        destroyOnHidden
        width={520}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowGuide(false)}>
            Got it
          </Button>,
        ]}
      >
        <p className="hms-modal-note">
          Room types are the categories you sell, for example Standard, Deluxe or Suite.
        </p>
        <Section title="Set up in this order">
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            <li>Create your room types here.</li>
            <li>Set a price for each type in Room Price.</li>
            <li>Add your rooms in Room List and give each one a type.</li>
          </ol>
        </Section>
      </Modal>

      <Modal
        title={currentRoomType ? "Edit room type" : "Add room type"}
        open={editModalOpen}
        onCancel={closeEdit}
        destroyOnHidden
        confirmLoading={saving}
        okText={currentRoomType ? "Save changes" : "Add room type"}
        onOk={handleSave}
        width={480}
      >
        <div className="hms-field">
          <label htmlFor="rt-name">Room type name</label>
          <Input
            id="rt-name"
            value={typeName}
            onChange={(e) => setTypeName(e.target.value)}
            placeholder="e.g. Standard, Deluxe, Suite"
            onPressEnter={handleSave}
          />
        </div>

        <div className="hms-field">
          <label htmlFor="rt-desc">Description</label>
          <Input.TextArea
            id="rt-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            rows={4}
          />
        </div>
      </Modal>
    </div>
  );
};

export default RoomTypesPage;
